import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import type {
  CategoryDefinitions,
  PropertyExtractionDefinitions,
} from "./types";

interface EditPromptsDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    categoryDefinitions: CategoryDefinitions,
    propertyExtractionDefinitions: PropertyExtractionDefinitions
  ) => Promise<void>;
  initialCategoryDefinitions: CategoryDefinitions;
  initialPropertyExtractionDefinitions: PropertyExtractionDefinitions;
}

export const EditPromptsDialog: React.FC<EditPromptsDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialCategoryDefinitions,
  initialPropertyExtractionDefinitions,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [categoryJsonText, setCategoryJsonText] = useState(
    JSON.stringify(initialCategoryDefinitions, null, 2)
  );
  const [propertyJsonText, setPropertyJsonText] = useState(
    JSON.stringify(initialPropertyExtractionDefinitions, null, 2)
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setCategoryJsonText(
        JSON.stringify(initialCategoryDefinitions, null, 2)
      );
      setPropertyJsonText(
        JSON.stringify(initialPropertyExtractionDefinitions, null, 2)
      );
      setError(null);
      setCurrentTab(0);
    }
  }, [open, initialCategoryDefinitions, initialPropertyExtractionDefinitions]);

  const validateCategoryDefinitions = (
    parsed: any
  ): { valid: boolean; error?: string } => {
    // Validate structure - should be an object with category keys
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {
        valid: false,
        error: "Invalid format: must be an object with category definitions",
      };
    }

    // Validate that each category has a prompt field
    for (const [key, value] of Object.entries(parsed)) {
      if (
        typeof value !== "object" ||
        value === null ||
        !("prompt" in value) ||
        typeof (value as any).prompt !== "string"
      ) {
        return {
          valid: false,
          error: `Invalid category "${key}": must have a "prompt" string field`,
        };
      }
    }

    return { valid: true };
  };

  const validatePropertyExtractionDefinitions = (
    parsed: any
  ): { valid: boolean; error?: string } => {
    // Validate structure - should be an object with category keys
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {
        valid: false,
        error:
          "Invalid format: must be an object with property extraction definitions",
      };
    }

    // Validate that each category has a properties array
    for (const [key, value] of Object.entries(parsed)) {
      if (
        typeof value !== "object" ||
        value === null ||
        !("properties" in value) ||
        !Array.isArray((value as any).properties)
      ) {
        return {
          valid: false,
          error: `Invalid category "${key}": must have a "properties" array field`,
        };
      }

      // Validate each property in the array
      const properties = (value as any).properties;
      for (let i = 0; i < properties.length; i++) {
        const prop = properties[i];
        if (
          typeof prop !== "object" ||
          prop === null ||
          !("name" in prop) ||
          !("prompt" in prop) ||
          typeof prop.name !== "string" ||
          typeof prop.prompt !== "string"
        ) {
          return {
            valid: false,
            error: `Invalid property at index ${i} in category "${key}": must have "name" and "prompt" string fields`,
          };
        }
      }
    }

    return { valid: true };
  };

  const handleSubmit = async () => {
    try {
      // Parse both JSONs
      const parsedCategories = JSON.parse(categoryJsonText);
      const parsedProperties = JSON.parse(propertyJsonText);

      // Validate category definitions
      const categoryValidation =
        validateCategoryDefinitions(parsedCategories);
      if (!categoryValidation.valid) {
        setError(`Category Error: ${categoryValidation.error}`);
        setCurrentTab(0);
        return;
      }

      // Validate property extraction definitions
      const propertyValidation = validatePropertyExtractionDefinitions(
        parsedProperties
      );
      if (!propertyValidation.valid) {
        setError(`Property Extraction Error: ${propertyValidation.error}`);
        setCurrentTab(1);
        return;
      }

      setError(null);
      setSubmitting(true);

      try {
        await onSubmit(
          parsedCategories as CategoryDefinitions,
          parsedProperties as PropertyExtractionDefinitions
        );
        onClose();
      } catch (submitError) {
        setError(
          submitError instanceof Error
            ? submitError.message
            : "Failed to submit"
        );
      } finally {
        setSubmitting(false);
      }
    } catch (parseError) {
      setError(
        parseError instanceof Error
          ? `JSON Parse Error: ${parseError.message}`
          : "Invalid JSON"
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          height: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Edit Definitions</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab label="Email Categories" />
          <Tab label="Property Extraction" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {currentTab === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Edit the category definitions as JSON. You can modify existing
              categories or add new ones. Each category must have a "prompt"
              field.
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={20}
              maxRows={30}
              value={categoryJsonText}
              onChange={(e) => setCategoryJsonText(e.target.value)}
              placeholder='{\n  "category_name": {\n    "prompt": "Category description..."\n  }\n}'
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                },
              }}
            />
          </Box>
        )}

        {currentTab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Edit the property extraction definitions as JSON. Each category
              can have a "properties" array. Each property must have "name" and
              "prompt" fields.
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={20}
              maxRows={30}
              value={propertyJsonText}
              onChange={(e) => setPropertyJsonText(e.target.value)}
              placeholder='{\n  "category_name": {\n    "properties": [\n      {\n        "name": "property_name",\n        "prompt": "Property extraction instructions..."\n      }\n    ]\n  }\n}'
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit Both"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
